import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { getMeta } from "./calendarTypes";
import { buildMonthGrid, isSameDay, formatHM, FR_DAYS_SHORT, groupByDay } from "./calendarHelpers";

/**
 * MonthView — premium calendar grid for desktop.
 * 6 rows × 7 cols. Days outside the current month are dimmed.
 */
export default function MonthView({ anchor, events, onDayClick, onEventClick }) {
  const today = useMemo(() => new Date(), []);
  const year = anchor.getFullYear();
  const month = anchor.getMonth();
  const days = useMemo(() => buildMonthGrid(year, month), [year, month]);
  const map = useMemo(() => groupByDay(events), [events]);

  return (
    <div
      className="rounded-2xl border border-border dark:border-border bg-card dark:bg-surface/40 overflow-hidden"
      data-testid="cal-month-view"
    >
      <div className="grid grid-cols-7 border-b border-border dark:border-border bg-secondary/50 dark:bg-surface/60">
        {FR_DAYS_SHORT.map((d) => (
          <div key={d} className="px-3 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground text-center">
            {d}.
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 auto-rows-[minmax(112px,1fr)]">
        {days.map((d) => {
          const inMonth = d.getMonth() === month;
          const isToday = isSameDay(d, today);
          const items = map[d.toDateString()] || [];
          return (
            <button
              key={d.toISOString()}
              onClick={() => onDayClick?.(d)}
              className={cn(
                "relative text-left p-2 border-r border-b border-border/70 dark:border-border hover:bg-accent/40 dark:hover:bg-secondary/60 transition-colors group",
                !inMonth && "opacity-40"
              )}
              data-testid={`month-cell-${d.toISOString().slice(0, 10)}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={cn(
                  "tabular-nums text-xs font-semibold flex items-center justify-center",
                  isToday
                    ? "h-6 w-6 rounded-full bg-violet-500 text-white"
                    : "text-foreground/85"
                )}>
                  {d.getDate()}
                </span>
                {items.length > 3 && (
                  <span className="text-[10px] text-muted-foreground/70">+{items.length - 3}</span>
                )}
              </div>
              <div className="space-y-1">
                {items.slice(0, 3).map((ev) => {
                  const meta = getMeta(ev.type);
                  return (
                    <div
                      key={ev.id}
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); onEventClick?.(ev); }}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onEventClick?.(ev); } }}
                      className={cn(
                        "text-[10px] rounded px-1.5 py-0.5 truncate border-l-2 cursor-pointer",
                        meta.bg, meta.border, meta.text
                      )}
                      data-testid={`month-event-${ev.id}`}
                    >
                      <span className="font-medium tabular-nums opacity-80 mr-1">{formatHM(ev.starts_at)}</span>
                      <span>{ev.title}</span>
                    </div>
                  );
                })}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
